import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Row, Col, Alert } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { UseFieldArrayAppend, UseFormSetValue } from 'react-hook-form';
import { AutorizacaoTrabalhoRemotoFormDto, PersonFormDto } from '@/modules/people/presentation/person-form.utils';
import { validarAutorizacaoTrabalhoRemoto } from '@/modules/people/presentation/forms/profiles/autorizacao-trabalho-remoto.utils';

interface AdicionarAutorizacaoTrabalhoRemotoModalProps {
  show: boolean;
  handleHide: () => void;
  initialData: AutorizacaoTrabalhoRemotoFormDto;
  itemIndex: number;
  existentes: AutorizacaoTrabalhoRemotoFormDto[];
  appendItem: UseFieldArrayAppend<PersonFormDto, 'defensor.autorizacoesTrabalhoRemoto'>;
  setValue: UseFormSetValue<PersonFormDto>;
}

const AdicionarAutorizacaoTrabalhoRemotoModal: React.FC<AdicionarAutorizacaoTrabalhoRemotoModalProps> = ({
  show,
  handleHide,
  initialData,
  itemIndex,
  existentes,
  appendItem,
  setValue,
}) => {
  const { register, handleSubmit, reset, watch, formState: { errors }, setError, clearErrors } =
    useForm<AutorizacaoTrabalhoRemotoFormDto>();
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const dataFinalValue = watch('data_final');
  const dataFinalRegister = register('data_final', {
    setValueAs: (v) => (v === '' || v == null ? null : v),
  });

  const onSubmit = (data: AutorizacaoTrabalhoRemotoFormDto) => {
    clearErrors();
    setErroGeral(null);

    const payload: AutorizacaoTrabalhoRemotoFormDto = {
      ...initialData,
      ...data,
      data_final: data.data_final ? data.data_final : null,
      anotacao: data.anotacao?.trim() ? data.anotacao.trim() : null,
    };

    const erro = validarAutorizacaoTrabalhoRemoto(payload, existentes, itemIndex);
    if (erro) {
      if (erro.includes('final')) {
        setError('data_final', { type: 'validate', message: erro });
      } else if (erro.includes('inicial')) {
        setError('data_inicial', { type: 'validate', message: erro });
      } else {
        setErroGeral(erro);
      }
      return;
    }

    if (itemIndex >= 0) {
      setValue(`defensor.autorizacoesTrabalhoRemoto.${itemIndex}`, payload, {
        shouldDirty: true,
        shouldTouch: true,
      });
    } else {
      appendItem(payload, { shouldFocus: false });
    }

    handleHide();
  };

  const handleSalvar = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    void handleSubmit(onSubmit)();
  };

  useEffect(() => {
    if (show) {
      setErroGeral(null);
      clearErrors();
      reset({
        id: initialData?.id,
        data_inicial: initialData?.data_inicial ?? '',
        data_final: initialData?.data_final ?? null,
        anotacao: initialData?.anotacao ?? '',
      });
    }
  }, [show, initialData, reset, clearErrors]);

  return (
    <Modal show={show} onHide={handleHide} centered>
      {/* Sem <form>: o modal fica dentro do form da página de edição. */}
      <Modal.Header closeButton className="py-2">
        <Modal.Title className="fs-6">
          {itemIndex >= 0 ? 'Editar autorização' : 'Nova autorização de trabalho remoto'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="py-2">
        {erroGeral && (
          <Alert variant="danger" className="py-2 px-2 small mb-2">
            {erroGeral}
          </Alert>
        )}
        <Row className="g-2">
          <Col xs={12}>
            <Form.Group>
              <Form.Label className="mb-1 small">Data inicial *</Form.Label>
              <Form.Control
                type="date"
                size="sm"
                {...register('data_inicial', { required: 'Informe a data inicial' })}
                isInvalid={!!errors.data_inicial}
              />
              <Form.Control.Feedback type="invalid">
                {errors.data_inicial?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col xs={12}>
            <Form.Group>
              <Form.Label className="mb-1 small">Data final</Form.Label>
              <Form.Control
                type="date"
                size="sm"
                name={dataFinalRegister.name}
                ref={dataFinalRegister.ref}
                onBlur={dataFinalRegister.onBlur}
                onChange={dataFinalRegister.onChange}
                value={dataFinalValue ?? ''}
                isInvalid={!!errors.data_final}
              />
              <Form.Control.Feedback type="invalid">
                {errors.data_final?.message}
              </Form.Control.Feedback>
              {!errors.data_final && (
                <Form.Text className="text-muted small">
                  Em branco = autorização em aberto.
                </Form.Text>
              )}
            </Form.Group>
          </Col>
          <Col xs={12}>
            <Form.Group>
              <Form.Label className="mb-1 small">Anotação</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                size="sm"
                maxLength={500}
                {...register('anotacao')}
              />
            </Form.Group>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer className="py-2">
        <Button variant="secondary" size="sm" type="button" onClick={handleHide}>
          Cancelar
        </Button>
        <Button variant="primary" size="sm" type="button" onClick={handleSalvar}>
          Confirmar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AdicionarAutorizacaoTrabalhoRemotoModal;
